import { useState } from "react";

export type AvatarSize = "xs" | "sm" | "md" | "lg";
export type AvatarVariant = "brand" | "neutral";

const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: "size-[22px] [font:var(--type-micro-sm)] [font-weight:var(--weight-semibold)]",
  sm: "size-7 [font:var(--type-micro)] [font-weight:var(--weight-semibold)]",
  md: "size-9 [font:var(--type-body-xs)] [font-weight:var(--weight-semibold)]",
  lg: "size-11 text-base font-semibold",
};

const VARIANT_CLASSES: Record<AvatarVariant, string> = {
  brand: "bg-brand-primary text-content-on-brand",
  neutral: "bg-content-primary text-content-inverse",
};

const STATUS_DOT_SIZE: Record<AvatarSize, string> = {
  xs: "size-1.5",
  sm: "size-2",
  md: "size-2.5",
  lg: "size-3",
};

export interface AvatarProps {
  initials: string;

  src?: string;
  alt?: string;

  size?: AvatarSize;

  variant?: AvatarVariant;

  status?: boolean;
}

export function Avatar({
  initials,
  src,
  alt = "",
  size = "md",
  variant = "neutral",
  status = false,
}: Readonly<AvatarProps>) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = src && !imageFailed;

  return (
    <span className="relative inline-grid shrink-0">
      <span
        className={[
          "grid place-items-center overflow-hidden rounded-full",
          SIZE_CLASSES[size],
          VARIANT_CLASSES[variant],
        ].join(" ")}
      >
        {showImage ? (
          <img src={src} alt={alt} className="size-full object-cover" onError={() => setImageFailed(true)} />
        ) : (
          <span className="pt-px leading-none">{initials}</span>
        )}
      </span>
      {status && (
        <span
          aria-hidden="true"
          className={[
            "absolute right-0 bottom-0 rounded-full bg-success ring-2 ring-surface-elevated",
            STATUS_DOT_SIZE[size],
          ].join(" ")}
        />
      )}
    </span>
  );
}
